import React, { useState } from 'react';
import { Modal, Button, Row, Col, Badge } from 'react-bootstrap';
import { ItemServiceCardDTO } from './ItemServiceCard';
import ServiceDetailsPreviewModal from './ViewItemTypeModal';

interface ViewItemServiceCardModuleProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  itemServiceCard: ItemServiceCardDTO;
}

interface SavedSchemaV2 {
  version: 2;
  title: string;
  tables: Array<{
    id?: string;
    title?: string;
    rows: number;
    fields: Array<{
      name: string;
      type: string;
      required: boolean;
      unit?: string;
      options?: string[];
    }>;
    values?: Record<string, any>;
  }>;
  metaBox: {
    show: boolean;
    fields: string[];
    values?: Record<string, string>;
  };
  detailsRow: {
    show: boolean;
    rightCols: number;
    values?: Record<string, any>;
  };
  footer: {
    columns: number;
    fields: Array<{ label: string; type: string }>;
    values?: Record<string, any>;
  };
}

const ViewItemServiceCardModule: React.FC<ViewItemServiceCardModuleProps> = ({
  show,
  onHide,
  itemServiceCard,
}) => {
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const parseServiceDetails = (serviceDetails?: string): SavedSchemaV2 | null => {
    if (!serviceDetails) return null;
    try {
      const parsed = JSON.parse(serviceDetails);
      if (parsed?.version === 2) return parsed as SavedSchemaV2;
      return null;
    } catch {
      console.warn('Invalid JSON in serviceDetails');
      return null;
    }
  };

  const serviceDetails = parseServiceDetails(itemServiceCard.serviceDetails);

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
        <Modal.Header closeButton className="bg-light border-0">
          <Modal.Title>
            <i className="bi bi-clipboard2-check me-2 text-primary" />
            Item Service Card #{itemServiceCard.id}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 py-3">
          <h6 className="text-info mb-3">General Information</h6>
          <Row className="gy-2">
            <Col md={6}>
              <strong>Item ID:</strong> <br />
              <span className="text-dark">{itemServiceCard.itemId || '-'}</span>
            </Col>
            <Col md={6}>
              <strong>Item Type ID:</strong> <br />
              <span className="text-dark">{itemServiceCard.itemTypeId || '-'}</span>
            </Col>
            <Col md={6}>
              <strong>Scheduled Start:</strong> <br />
              <span className="text-dark">
                {itemServiceCard.scheduledDateTime
                  ? new Date(itemServiceCard.scheduledDateTime).toLocaleString()
                  : '-'}
              </span>
            </Col>
            <Col md={6}>
              <strong>Scheduled End:</strong> <br />
              <span className="text-dark">
                {itemServiceCard.scheduledEndDateTime
                  ? new Date(itemServiceCard.scheduledEndDateTime).toLocaleString()
                  : '-'}
              </span>
            </Col>
            <Col md={6}>
              <strong>Accountable Person ID:</strong> <br />
              <span className="text-dark">{itemServiceCard.accountablePersonId || '-'}</span>
            </Col>
            <Col md={6}>
              <strong>Status:</strong> <br />
              <Badge
                bg={
                  itemServiceCard.status?.toLowerCase() === 'completed'
                    ? 'success'
                    : itemServiceCard.status?.toLowerCase() === 'pending'
                    ? 'warning'
                    : 'dark'
                }
              >
                {itemServiceCard.status || 'Unknown'}
              </Badge>
            </Col>
            <Col md={12}>
              <strong>Comment:</strong> <br />
              <span className="text-dark">{itemServiceCard.comment || '-'}</span>
            </Col>
            <Col md={12}>
              <strong>Service Details:</strong> <br />
              {serviceDetails ? (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setShowPreviewModal(true)}
                >
                  <i className="bi bi-printer me-1" />
                  Preview / Print
                </Button>
              ) : (
                <span className="text-dark">-</span>
              )}
            </Col>
          </Row>

          <hr className="my-3" />

          <h6 className="text-info mb-3">Audit Information</h6>
          <Row className="gy-2">
            <Col md={6}>
              <strong>Created By:</strong> <br />
              <span className="text-dark">{itemServiceCard.createdBy || '-'}</span>
            </Col>
            <Col md={6}>
              <strong>Created At:</strong> <br />
              <span className="text-dark">
                {itemServiceCard.createdAt
                  ? new Date(itemServiceCard.createdAt).toLocaleString()
                  : '-'}
              </span>
            </Col>
            <Col md={6}>
              <strong>Updated By:</strong> <br />
              <span className="text-dark">{itemServiceCard.updatedBy || '-'}</span>
            </Col>
            <Col md={6}>
              <strong>Updated At:</strong> <br />
              <span className="text-dark">
                {itemServiceCard.updatedAt
                  ? new Date(itemServiceCard.updatedAt).toLocaleString()
                  : '-'}
              </span>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="border-0 px-4 pb-3">
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {serviceDetails && (
        <ServiceDetailsPreviewModal
          show={showPreviewModal}
          onHide={() => setShowPreviewModal(false)}
          serviceDetails={serviceDetails}
        />
      )}
    </>
  );
};

export default ViewItemServiceCardModule;
